import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Server, Play, Square, RotateCcw, AlertTriangle, Monitor, HardDrive,
  Cpu, MemoryStick, Wifi, BarChart2, Terminal, Archive, Camera, RefreshCw,
  ShieldAlert, Network, Globe, LogOut, ChevronRight, Clock, Download,
  Trash2, RotateCw, Check, X, Zap, Eye, EyeOff, Copy, ExternalLink,
  HelpCircle, Plus, Pencil, Power,
} from 'lucide-react';
import { useTheme } from '@/lib/theme';
import { toast } from 'sonner';

// ── Mock data ──────────────────────────────────────────────────────────────
const MOCK_SERVER = {
  id: 'vps-4821',
  name: 'KVM VPS Ryzen 2500X',
  hostname: 'vps4821.deinserverhost.de',
  status: 'running',
  os: { name: 'Debian 12 (Bookworm)', icon: '🐧' },
  ip4: '185.216.48.114',
  ip6: '2a00:1188:3:1::114',
  ptr: 'vps4821.deinserverhost.de',
  cpu: { cores: 4, used: 23 },
  ram: { total: 4096, used: 1843 },
  disk: { total: 80, used: 28.4 },
  traffic: { total: 250, used: 47.2, unit: 'GB' },
  uptime: '14 Tage, 3 Stunden',
  location: 'Frankfurt, DE',
  package: 'KVM VPS Ryzen 2500X',
  renewalDate: '15.04.2026',
  hypervisor: 'Proxmox VE 8.1',
  vmid: 4821,
  node: 'pve01.fsn.dsh',
  password: 'xK9!mP2#nQr7vTw$',
};

const MOCK_BACKUPS = [
  { id: 'bkp-3', name: 'Automatisches Backup', date: '15.03.2026 02:00', size: '4.2 GB', type: 'auto' },
  { id: 'bkp-2', name: 'Automatisches Backup', date: '10.03.2026 02:00', size: '4.1 GB', type: 'auto' },
  { id: 'bkp-1', name: 'Automatisches Backup', date: '05.03.2026 02:00', size: '3.9 GB', type: 'auto' },
];

const MOCK_SNAPSHOTS = [
  { id: 'snap-2', name: 'vor-update-kernel', date: '14.03.2026 18:34', size: '4.8 GB', note: 'Vor Kernel-Upgrade auf 6.7' },
  { id: 'snap-1', name: 'clean-debian-install', date: '28.02.2026 11:00', size: '3.1 GB', note: 'Frische Debian-Installation' },
];

const OS_OPTIONS = [
  { id: 'debian12',       name: 'Debian 12 (Bookworm)',  category: 'Linux',   arch: ['x86_64'] },
  { id: 'ubuntu2404',     name: 'Ubuntu 24.04 LTS',      category: 'Linux',   arch: ['x86_64'] },
  { id: 'ubuntu2204',     name: 'Ubuntu 22.04 LTS',      category: 'Linux',   arch: ['x86_64'] },
  { id: 'almalinux9',     name: 'AlmaLinux 9',           category: 'Linux',   arch: ['x86_64'] },
  { id: 'rocky9',         name: 'Rocky Linux 9',         category: 'Linux',   arch: ['x86_64'] },
  { id: 'centos-stream9', name: 'CentOS Stream 9',       category: 'Linux',   arch: ['x86_64'] },
  { id: 'win2022',        name: 'Windows Server 2022',   category: 'Windows', arch: ['x86_64'], extra: '+€5/Monat' },
  { id: 'win2019',        name: 'Windows Server 2019',   category: 'Windows', arch: ['x86_64'], extra: '+€5/Monat' },
  { id: 'freebsd14',      name: 'FreeBSD 14',            category: 'BSD',     arch: ['x86_64'] },
];

const RESCUE_ISOS = [
  { id: 'grml', name: 'GRML 2024.02 (Linux Rescue)', size: '480 MB' },
  { id: 'sysrescue', name: 'SystemRescue 11.01', size: '660 MB' },
  { id: 'winpe', name: 'WinPE 11 Rescue', size: '860 MB' },
  { id: 'proxmox-ipxe', name: 'Proxmox iPXE Boot', size: 'Online' },
];

const FIREWALL_RULES = [
  { id: 1, dir: 'IN',  proto: 'TCP', port: '22',    source: 'Any',    action: 'ACCEPT', note: 'SSH' },
  { id: 2, dir: 'IN',  proto: 'TCP', port: '80,443', source: 'Any',   action: 'ACCEPT', note: 'HTTP/S' },
  { id: 3, dir: 'IN',  proto: 'ICMP', port: '–',    source: 'Any',    action: 'ACCEPT', note: 'Ping' },
  { id: 4, dir: 'IN',  proto: 'ANY', port: 'Any',   source: 'Any',    action: 'DROP',   note: 'Default deny' },
  { id: 5, dir: 'OUT', proto: 'ANY', port: 'Any',   source: '185.216.48.114', action: 'ACCEPT', note: 'Allow all out' },
];

// ── Sub-components ─────────────────────────────────────────────────────────

function BlurReveal({ value, mono = false, small = false, copyable = true }) {
  const [revealed, setRevealed] = useState(false);
  return (
    <span className="inline-flex items-center gap-1">
      <span
        className={`${mono ? 'font-mono' : ''} ${small ? 'text-xs' : ''} transition-[filter] duration-200`}
        style={{ filter: revealed ? 'none' : 'blur(5px)', userSelect: revealed ? 'text' : 'none' }}
      >
        {value}
      </span>
      <button
        onClick={() => setRevealed(v => !v)}
        className="shrink-0 p-0.5 rounded text-muted-foreground hover:text-primary transition-colors"
        title={revealed ? 'Verbergen' : 'Anzeigen'}
        data-testid="blur-reveal-toggle"
      >
        {revealed ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
      </button>
      {copyable && revealed && (
        <button
          className="shrink-0 p-0.5 rounded text-muted-foreground hover:text-primary transition-colors"
          onClick={() => { navigator.clipboard.writeText(value); toast.success('Kopiert!'); }}
          title="Kopieren"
          data-testid="blur-reveal-copy"
        >
          <Copy className="w-3 h-3" />
        </button>
      )}
    </span>
  );
}

function StatGauge({ label, value, max, unit, icon: Icon, color = 'primary' }) {
  const pct = Math.min(Math.round((value / max) * 100), 100);
  const colorMap = { primary: '#FF6B00', green: '#22C55E', blue: '#3B82F6', red: '#EF4444' };
  const barColor = pct > 80 ? colorMap.red : pct > 60 ? colorMap.primary : colorMap.green;

  return (
    <div className="dsh-stat-card glass rounded-xl border border-border p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-primary shrink-0" />
          <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{label}</span>
        </div>
        <span className="text-xs font-bold" style={{ color: barColor }}>{pct}%</span>
      </div>
      <div className="flex items-baseline gap-1 mb-3">
        <span className="text-2xl font-black">{typeof value === 'number' && value < 10 ? value.toFixed(1) : value}</span>
        <span className="text-xs text-muted-foreground">/ {max} {unit}</span>
      </div>
      <div className="w-full h-1.5 rounded-full bg-muted overflow-hidden">
        <motion.div
          className="h-full rounded-full"
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          style={{ backgroundColor: barColor }}
        />
      </div>
    </div>
  );
}

function TabOverview({ server }) {
  return (
    <div className="space-y-6">
      {/* Resource Gauges */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatGauge label="CPU" value={server.cpu.used} max={100} unit="%" icon={Cpu} />
        <StatGauge label="RAM" value={(server.ram.used / 1024).toFixed(1)} max={(server.ram.total / 1024).toFixed(0)} unit="GB" icon={MemoryStick} />
        <StatGauge label="Disk" value={server.disk.used} max={server.disk.total} unit="GB" icon={HardDrive} />
        <StatGauge label="Traffic" value={server.traffic.used} max={server.traffic.total} unit="GB" icon={BarChart2} />
      </div>

      {/* Server Info */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="glass rounded-xl border border-border p-5">
          <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-4">Server-Informationen</h3>
          <dl className="space-y-3">
            {[
              { label: 'Betriebssystem', value: server.os.name },
              { label: 'Laufzeit', value: server.uptime, icon: Clock },
              { label: 'Standort', value: server.location, icon: Globe },
              { label: 'Hypervisor', value: server.hypervisor },
              { label: 'VM-ID', value: `#${server.vmid}` },
              { label: 'Node', value: server.node, mono: true },
            ].map(({ label, value, icon: Icon, mono }) => (
              <div key={label} className="flex items-center justify-between text-sm">
                <dt className="text-muted-foreground">{label}</dt>
                <dd className={`font-medium ${mono ? 'font-mono text-xs' : ''}`}>{value}</dd>
              </div>
            ))}
          </dl>
        </div>

        <div className="glass rounded-xl border border-border p-5">
          <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-4">Netzwerk</h3>
          <dl className="space-y-3 text-sm">
            <div className="flex items-center justify-between gap-2">
              <dt className="text-muted-foreground shrink-0">IPv4</dt>
              <dd className="flex items-center"><BlurReveal value={server.ip4} mono small /></dd>
            </div>
            <div className="flex items-center justify-between gap-2">
              <dt className="text-muted-foreground shrink-0">IPv6</dt>
              <dd className="flex items-center max-w-[200px]"><BlurReveal value={server.ip6} mono small /></dd>
            </div>
            {[
              { label: 'PTR / rDNS', value: server.ptr, mono: true, small: true },
              { label: 'Paket', value: server.package },
              { label: 'Verlängerung', value: server.renewalDate },
            ].map(({ label, value, mono, small }) => (
              <div key={label} className="flex items-center justify-between gap-2">
                <dt className="text-muted-foreground shrink-0">{label}</dt>
                <dd className={`${mono ? 'font-mono' : ''} ${small ? 'text-xs' : ''} font-medium`}>{value}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </div>
  );
}

function TabConsole({ server }) {
  return (
    <div className="space-y-4">
      <div className="glass rounded-xl border border-border p-6 text-center">
        <Terminal className="w-16 h-16 text-primary mx-auto mb-4 opacity-70" />
        <h3 className="text-xl font-bold mb-2">noVNC Web-Konsole</h3>
        <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
          Direkter Zugriff auf die Grafikkonsole deines Servers – unabhängig vom Betriebssystem-Status.
          Ideal für Recovery und Ersteinrichtung.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <button className="flex items-center gap-2 px-6 py-3 bg-primary hover:bg-primary/90 text-white rounded-lg font-bold text-sm uppercase tracking-wider transition-colors" style={{ boxShadow: '0 0 20px rgba(255,107,0,0.3)' }}>
            <ExternalLink className="w-4 h-4" />
            Konsole öffnen (noVNC)
          </button>
          <button className="flex items-center gap-2 px-6 py-3 bg-muted hover:bg-muted/80 rounded-lg font-bold text-sm uppercase tracking-wider transition-colors">
            <Terminal className="w-4 h-4" />
            SSH-Verbindung
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="glass rounded-xl border border-border p-5">
          <h4 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-3">SSH Quick-Connect</h4>
          <div className="bg-muted rounded-lg p-3 font-mono text-sm flex items-center justify-between gap-2">
            <BlurReveal value={`ssh root@${server.ip4}`} mono copyable />
          </div>
        </div>
        <div className="glass rounded-xl border border-border p-5">
          <h4 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-3">Verbindungsinfos</h4>
          <div className="space-y-2.5 text-sm">
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Host</span>
              <BlurReveal value={server.ip4} mono small />
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Port</span>
              <code className="font-mono text-xs">22</code>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">User</span>
              <code className="font-mono text-xs">root</code>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Passwort</span>
              <BlurReveal value={server.password} mono small />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TabBackups() {
  const [restoring, setRestoring] = useState(null);
  const doRestore = (id) => { setRestoring(id); setTimeout(() => { setRestoring(null); toast.success('Wiederherstellung gestartet!'); }, 2000); };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">3 Backups verfügbar (automatisch täglich)</p>
        <button className="flex items-center gap-2 text-sm font-bold text-primary hover:text-primary/80 transition-colors">
          <Plus className="w-4 h-4" />
          Manuelles Backup
        </button>
      </div>

      <div className="glass rounded-xl border border-border overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-muted-foreground">Datum</th>
              <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-muted-foreground hidden sm:table-cell">Name</th>
              <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-muted-foreground">Größe</th>
              <th className="text-right p-4 text-xs font-bold uppercase tracking-wider text-muted-foreground">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {MOCK_BACKUPS.map((bkp) => (
              <tr key={bkp.id} className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors">
                <td className="p-4 text-sm font-mono">{bkp.date}</td>
                <td className="p-4 text-sm text-muted-foreground hidden sm:table-cell">{bkp.name}</td>
                <td className="p-4 text-sm">{bkp.size}</td>
                <td className="p-4 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => doRestore(bkp.id)}
                      disabled={!!restoring}
                      className="flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors disabled:opacity-50"
                    >
                      {restoring === bkp.id ? <span className="w-3 h-3 border-2 border-primary/30 border-t-primary rounded-full animate-spin" /> : <RotateCw className="w-3 h-3" />}
                      Wiederherstellen
                    </button>
                    <button className="p-1.5 rounded-lg text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-colors">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TabSnapshots() {
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [snapName, setSnapName] = useState('');

  const createSnap = () => {
    if (!snapName.trim()) return;
    setCreating(true);
    setTimeout(() => { setCreating(false); setShowCreate(false); setSnapName(''); toast.success(`Snapshot "${snapName}" erstellt!`); }, 2500);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{MOCK_SNAPSHOTS.length} Snapshots gespeichert</p>
        <button
          onClick={() => setShowCreate(!showCreate)}
          className="flex items-center gap-2 text-sm font-bold text-primary hover:text-primary/80 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Snapshot erstellen
        </button>
      </div>

      <AnimatePresence>
        {showCreate && (
          <motion.div
            initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
            className="glass rounded-xl border border-primary/30 p-5 overflow-hidden"
          >
            <h4 className="text-sm font-bold mb-3">Neuen Snapshot erstellen</h4>
            <p className="text-xs text-muted-foreground mb-3">Der Server bleibt während des Snapshots online (Live-Snapshot).</p>
            <div className="flex gap-3">
              <input
                value={snapName} onChange={e => setSnapName(e.target.value)}
                placeholder="z.B. vor-update-nginx"
                className="flex-1 bg-muted border border-border rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:border-primary transition-colors"
              />
              <button
                onClick={createSnap} disabled={creating || !snapName.trim()}
                className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg font-bold text-sm disabled:opacity-50 transition-colors"
              >
                {creating ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Camera className="w-4 h-4" />}
                Erstellen
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="glass rounded-xl border border-border overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-muted-foreground">Name</th>
              <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-muted-foreground hidden md:table-cell">Notiz</th>
              <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-muted-foreground">Datum</th>
              <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-muted-foreground hidden sm:table-cell">Größe</th>
              <th className="text-right p-4 text-xs font-bold uppercase tracking-wider text-muted-foreground">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {MOCK_SNAPSHOTS.map((snap) => (
              <tr key={snap.id} className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors">
                <td className="p-4 text-sm font-mono font-bold">{snap.name}</td>
                <td className="p-4 text-xs text-muted-foreground hidden md:table-cell">{snap.note}</td>
                <td className="p-4 text-xs font-mono text-muted-foreground">{snap.date}</td>
                <td className="p-4 text-sm hidden sm:table-cell">{snap.size}</td>
                <td className="p-4 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button className="flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 transition-colors" onClick={() => toast.info('Rollback auf ' + snap.name + ' eingeleitet…')}>
                      <RotateCw className="w-3 h-3" />
                      Rollback
                    </button>
                    <button className="p-1.5 rounded-lg text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-colors">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const OS_ICON_CONFIG = {
  debian12:         { slug: 'debian',     bg: '#A80030' },
  ubuntu2404:       { slug: 'ubuntu',     bg: '#E95420' },
  ubuntu2204:       { slug: 'ubuntu',     bg: '#C0430E' },
  almalinux9:       { slug: 'almalinux', bg: '#1A4F8A' },
  rocky9:           { slug: 'rockylinux', bg: '#10B981' },
  'centos-stream9': { slug: 'centos',    bg: '#932279' },
  win2022:          { slug: 'windows',   bg: '#0078D4' },
  win2019:          { slug: 'windows',   bg: '#0063B1' },
  freebsd14:        { slug: 'freebsd',   bg: '#AB2B28' },
};

const CDN = 'https://cdnjs.cloudflare.com/ajax/libs/simple-icons/15.16.0';

function OsIcon({ id, size = 40 }) {
  const cfg = OS_ICON_CONFIG[id];
  const pad = Math.round(size * 0.22);
  if (!cfg) return (
    <div style={{ width: size, height: size, borderRadius: '50%', background: '#6B7280', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Server style={{ width: size * 0.55, height: size * 0.55, color: 'white' }} />
    </div>
  );
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', background: cfg.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, padding: pad }}>
      <img
        src={`${CDN}/${cfg.slug}.svg`}
        alt={cfg.slug}
        style={{ width: '100%', height: '100%', filter: 'brightness(0) invert(1)', display: 'block' }}
        loading="lazy"
      />
    </div>
  );
}

function TabOsInstall() {
  const [selected, setSelected] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [filterCat, setFilterCat] = useState('Alle');
  const categories = ['Alle', 'Linux', 'Windows', 'BSD'];

  const filtered = OS_OPTIONS.filter(o => filterCat === 'Alle' || o.category === filterCat);

  const doInstall = () => {
    setInstalling(true);
    setTimeout(() => { setInstalling(false); setConfirm(false); setSelected(null); toast.success('OS-Installation gestartet! Der Server wird neu gestartet.'); }, 3000);
  };

  return (
    <div className="space-y-5">
      <div className="glass rounded-xl border border-yellow-500/30 bg-yellow-500/5 p-4 flex gap-3">
        <AlertTriangle className="w-5 h-5 text-yellow-500 shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-bold text-yellow-500 mb-0.5">Achtung: Alle Daten werden gelöscht</p>
          <p className="text-xs text-muted-foreground">Eine Neuinstallation überschreibt das komplette Betriebssystem. Erstelle vorher ein Backup oder Snapshot.</p>
        </div>
      </div>

      {/* Category Filter */}
      <div className="flex gap-2">
        {categories.map(cat => (
          <button key={cat} onClick={() => setFilterCat(cat)}
            className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-colors ${filterCat === cat ? 'bg-primary text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}>
            {cat}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
        {filtered.map((os) => (
          <button
            key={os.id}
            onClick={() => { setSelected(os); setConfirm(true); }}
            className={`relative flex flex-col items-center gap-2 p-4 rounded-xl border text-center transition-all ${
              selected?.id === os.id
                ? 'border-primary bg-primary/10 shadow-lg shadow-primary/10'
                : 'border-border glass hover:border-primary/50 hover:bg-primary/5'
            }`}
          >
            <span className="mb-1"><OsIcon id={os.id} size={40} /></span>
            <span className="text-xs font-bold leading-tight">{os.name}</span>
            {os.extra && <span className="text-xs text-primary font-bold">{os.extra}</span>}
          </button>
        ))}
      </div>

      <AnimatePresence>
        {confirm && selected && (
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }}
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
          >
            <div className="glass rounded-2xl border border-border w-full max-w-md p-8">
              <div className="text-center mb-6">
                <OsIcon id={selected.id} size={56} />
                <h3 className="text-xl font-black">Installation bestätigen</h3>
                <p className="text-sm text-muted-foreground mt-2">
                  <strong>{selected.name}</strong> wird installiert.<br />
                  Alle Daten auf dem Server werden unwiderruflich gelöscht.
                </p>
              </div>
              <div className="bg-muted rounded-xl p-4 mb-6 text-sm space-y-2">
                <div className="flex justify-between"><span className="text-muted-foreground">Aktuelles OS</span><span>Debian 12</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Neues OS</span><span className="font-bold text-primary">{selected.name}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Disk-Partition</span><span>Komplette Disk (/dev/sda)</span></div>
              </div>
              <div className="flex gap-3">
                <button onClick={() => { setConfirm(false); setSelected(null); }} className="flex-1 py-3 rounded-xl border border-border font-bold text-sm hover:bg-muted transition-colors">
                  Abbrechen
                </button>
                <button
                  onClick={doInstall} disabled={installing}
                  className="flex-1 py-3 rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-50 transition-colors"
                >
                  {installing ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  {installing ? 'Installiere…' : 'Jetzt installieren'}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function TabRescue({ server }) {
  const [rescueActive, setRescueActive] = useState(false);
  const [selectedIso, setSelectedIso] = useState(RESCUE_ISOS[0].id);
  const [loading, setLoading] = useState(false);

  const toggle = () => {
    setLoading(true);
    setTimeout(() => {
      setRescueActive(!rescueActive);
      setLoading(false);
      toast[rescueActive ? 'info' : 'success'](rescueActive ? 'Rescue-Modus deaktiviert' : 'Rescue-Modus aktiviert! Server neu starten zum Laden.');
    }, 1800);
  };

  return (
    <div className="space-y-5">
      <div className={`glass rounded-xl border p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 ${rescueActive ? 'border-yellow-500/40 bg-yellow-500/5' : 'border-border'}`}>
        <div className="flex items-start gap-4">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${rescueActive ? 'bg-yellow-500/20' : 'bg-muted'}`}>
            <ShieldAlert className={`w-6 h-6 ${rescueActive ? 'text-yellow-500' : 'text-muted-foreground'}`} />
          </div>
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h3 className="font-bold">Rescue-Modus</h3>
              <span className={`flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full ${rescueActive ? 'bg-yellow-500/15 text-yellow-500 border border-yellow-500/30' : 'bg-muted text-muted-foreground border border-border'}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${rescueActive ? 'bg-yellow-500 animate-pulse' : 'bg-muted-foreground'}`} />
                {rescueActive ? 'Aktiv' : 'Inaktiv'}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              {rescueActive
                ? 'Der Server wird beim nächsten Neustart vom Rescue-ISO booten.'
                : 'Aktiviere den Rescue-Modus, um beim nächsten Neustart ein Live-System zu booten.'}
            </p>
          </div>
        </div>
        <button
          onClick={toggle} disabled={loading}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-sm uppercase tracking-wider transition-colors disabled:opacity-50 shrink-0 ${
            rescueActive ? 'bg-muted hover:bg-muted/80 text-foreground' : 'bg-yellow-500 hover:bg-yellow-600 text-black'
          }`}
        >
          {loading ? <span className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" /> : <Power className="w-4 h-4" />}
          {rescueActive ? 'Deaktivieren' : 'Aktivieren'}
        </button>
      </div>

      <div className="glass rounded-xl border border-border p-5">
        <h4 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-4">Rescue-ISO auswählen</h4>
        <div className="grid sm:grid-cols-2 gap-3">
          {RESCUE_ISOS.map(iso => (
            <label key={iso.id} className={`flex items-center gap-3 p-3.5 rounded-xl border cursor-pointer transition-colors ${selectedIso === iso.id ? 'border-primary/50 bg-primary/5' : 'border-border hover:border-primary/30'}`}>
              <input type="radio" name="rescue-iso" value={iso.id} checked={selectedIso === iso.id} onChange={() => setSelectedIso(iso.id)} className="accent-primary" />
              <div>
                <p className="text-sm font-bold">{iso.name}</p>
                <p className="text-xs text-muted-foreground">{iso.size}</p>
              </div>
            </label>
          ))}
        </div>
      </div>

      <div className="glass rounded-xl border border-blue-500/20 bg-blue-500/5 p-4 flex gap-3">
        <HelpCircle className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" />
        <div className="text-xs text-muted-foreground leading-relaxed">
          <strong className="text-blue-400">Wie funktioniert das?</strong><br />
          1. Wähle ein Rescue-ISO aus · 2. Aktiviere den Rescue-Modus · 3. Starte den Server neu (über Powerverwaltung oben) · 4. Verbinde dich über die noVNC-Konsole
        </div>
      </div>
    </div>
  );
}

function TabNetwork({ server }) {
  const [editPtr, setEditPtr] = useState(false);
  const [ptrValue, setPtrValue] = useState(server.ptr);

  return (
    <div className="space-y-5">
      {/* IP Addresses */}
      <div className="glass rounded-xl border border-border overflow-hidden">
        <div className="px-5 py-3 border-b border-border flex items-center justify-between">
          <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">IP-Adressen</h4>
          <button className="flex items-center gap-1.5 text-xs font-bold text-primary hover:text-primary/80 transition-colors">
            <Plus className="w-3.5 h-3.5" /> Zusätzliche IP
          </button>
        </div>
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-muted-foreground">IP-Adresse</th>
              <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-muted-foreground hidden sm:table-cell">Typ</th>
              <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-muted-foreground hidden md:table-cell">Gateway</th>
              <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-muted-foreground">PTR / rDNS</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-border hover:bg-muted/30 transition-colors">
              <td className="p-4 font-mono text-sm"><BlurReveal value={server.ip4} mono small /></td>
              <td className="p-4 hidden sm:table-cell"><span className="text-xs font-bold px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">IPv4</span></td>
              <td className="p-4 font-mono text-xs text-muted-foreground hidden md:table-cell">185.216.48.1</td>
              <td className="p-4">
                {editPtr ? (
                  <div className="flex gap-2">
                    <input value={ptrValue} onChange={e => setPtrValue(e.target.value)} className="flex-1 bg-muted border border-primary rounded px-2 py-1 font-mono text-xs focus:outline-none" />
                    <button onClick={() => { setEditPtr(false); toast.success('PTR gespeichert'); }} className="px-2 py-1 bg-primary rounded text-white"><Check className="w-3 h-3" /></button>
                    <button onClick={() => setEditPtr(false)} className="px-2 py-1 bg-muted rounded"><X className="w-3 h-3" /></button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-muted-foreground">{ptrValue}</span>
                    <button onClick={() => setEditPtr(true)} className="text-muted-foreground hover:text-primary transition-colors"><Pencil className="w-3 h-3" /></button>
                  </div>
                )}
              </td>
            </tr>
            <tr className="hover:bg-muted/30 transition-colors">
              <td className="p-4"><BlurReveal value={server.ip6} mono small /></td>
              <td className="p-4 hidden sm:table-cell"><span className="text-xs font-bold px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-400 border border-blue-500/20">IPv6</span></td>
              <td className="p-4 font-mono text-xs text-muted-foreground hidden md:table-cell">2a00:1188:3:1::1</td>
              <td className="p-4 font-mono text-xs text-muted-foreground">–</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Firewall */}
      <div className="glass rounded-xl border border-border overflow-hidden">
        <div className="px-5 py-3 border-b border-border flex items-center justify-between">
          <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Firewall-Regeln</h4>
          <button className="flex items-center gap-1.5 text-xs font-bold text-primary hover:text-primary/80 transition-colors">
            <Plus className="w-3.5 h-3.5" /> Regel hinzufügen
          </button>
        </div>
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              {['Richtung', 'Protokoll', 'Port', 'Quelle', 'Aktion', 'Notiz', ''].map(h => (
                <th key={h} className="text-left p-3 text-xs font-bold uppercase tracking-wider text-muted-foreground first:pl-5">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {FIREWALL_RULES.map(rule => (
              <tr key={rule.id} className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors">
                <td className="p-3 pl-5">
                  <span className={`text-xs font-bold px-2 py-0.5 rounded ${rule.dir === 'IN' ? 'bg-blue-500/10 text-blue-400' : 'bg-purple-500/10 text-purple-400'}`}>{rule.dir}</span>
                </td>
                <td className="p-3 text-xs font-mono">{rule.proto}</td>
                <td className="p-3 text-xs font-mono">{rule.port}</td>
                <td className="p-3 text-xs text-muted-foreground">{rule.source}</td>
                <td className="p-3">
                  <span className={`text-xs font-bold px-2 py-0.5 rounded ${rule.action === 'ACCEPT' ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'}`}>{rule.action}</span>
                </td>
                <td className="p-3 text-xs text-muted-foreground">{rule.note}</td>
                <td className="p-3">
                  <button className="text-muted-foreground hover:text-red-500 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────
export default function WhmcsVpsPreview() {
  const { theme, setTheme } = useTheme();
  const [server, setServer] = useState(MOCK_SERVER);
  const [activeTab, setActiveTab] = useState('overview');
  const [powerLoading, setPowerLoading] = useState(null);

  const tabs = [
    { id: 'overview',   label: 'Übersicht',         icon: BarChart2 },
    { id: 'console',    label: 'Konsole',            icon: Terminal },
    { id: 'backups',    label: 'Backups',            icon: Archive },
    { id: 'snapshots',  label: 'Snapshots',          icon: Camera },
    { id: 'os-install', label: 'OS-Neuinstallation', icon: RefreshCw },
    { id: 'rescue',     label: 'Rescue-Modus',       icon: ShieldAlert },
    { id: 'network',    label: 'Netzwerk',           icon: Network },
  ];

  const powerAction = (action) => {
    setPowerLoading(action);
    const messages = { start: 'Server wird gestartet…', stop: 'Server wird gestoppt…', restart: 'Server wird neugestartet…', reset: 'Hard-Reset wird ausgeführt…' };
    toast.info(messages[action]);
    setTimeout(() => {
      setPowerLoading(null);
      setServer(s => ({ ...s, status: action === 'stop' ? 'stopped' : 'running' }));
      toast.success(action === 'stop' ? 'Server gestoppt' : 'Server gestartet');
    }, 2500);
  };

  const statusColor = server.status === 'running' ? 'text-green-500' : 'text-red-500';
  const statusBg    = server.status === 'running' ? 'bg-green-500' : 'bg-red-500';
  const statusLabel = server.status === 'running' ? 'Running' : 'Stopped';

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      {/* ── Top Bar ── */}
      <header className="h-14 bg-card border-b border-border flex items-center justify-between px-4 md:px-6 gap-4 sticky top-0 z-40">
        <div className="flex items-center gap-4">
          <img
            src={theme === 'dark' ? '/white.svg' : '/black.svg'}
            alt="DeinServerHost" className="h-7"
          />
          <div className="hidden sm:flex items-center gap-1 text-muted-foreground text-sm">
            <ChevronRight className="w-3.5 h-3.5" />
            <span>Kundenbereich</span>
            <ChevronRight className="w-3.5 h-3.5" />
            <span className="text-foreground font-medium">vServer</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            className="p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground"
            title="Theme wechseln"
          >
            {theme === 'dark' ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
          </button>
          <div className="flex items-center gap-2 text-sm">
            <div className="w-7 h-7 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-center text-xs font-bold text-primary">M</div>
            <span className="hidden md:inline text-muted-foreground">Max Muster</span>
          </div>
          <a href="/" className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors ml-1 px-3 py-1.5 rounded-lg hover:bg-muted">
            <LogOut className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Logout</span>
          </a>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* ── Sidebar ── */}
        <aside className="hidden md:flex flex-col w-56 bg-card border-r border-border shrink-0">
          <div className="p-3 border-b border-border">
            <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground px-2 mb-2">Services</p>
            {[
              { label: server.name, active: true, status: server.status },
              { label: 'KVM VPS Ryzen (kl.)', active: false, status: 'stopped' },
              { label: 'Gameserver CS2', active: false, status: 'running' },
            ].map((svc, i) => (
              <button
                key={i}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-left text-sm transition-colors mb-0.5 ${svc.active ? 'bg-primary/10 text-primary font-semibold' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
              >
                <span className={`w-2 h-2 rounded-full shrink-0 ${svc.status === 'running' ? 'bg-green-500' : 'bg-red-400'}`} />
                <span className="truncate">{svc.label}</span>
              </button>
            ))}
          </div>
          <nav className="p-3 flex-1">
            <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground px-2 mb-2 mt-1">Navigation</p>
            {[
              { label: 'Dashboard', icon: BarChart2 },
              { label: 'Rechnungen', icon: Archive },
              { label: 'Support', icon: HelpCircle },
              { label: 'Profil', icon: Globe },
            ].map(item => (
              <button key={item.label} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-colors mb-0.5">
                <item.icon className="w-4 h-4 shrink-0" />
                {item.label}
              </button>
            ))}
          </nav>
          <div className="p-3 border-t border-border">
            <a href="/design-system" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-xs text-muted-foreground hover:text-primary transition-colors px-2 py-1.5">
              <Palette className="w-3.5 h-3.5" />
              Design System
              <ExternalLink className="w-3 h-3 ml-auto" />
            </a>
          </div>
        </aside>

        {/* ── Main ── */}
        <main className="flex-1 overflow-auto p-4 md:p-6">

          {/* Server Header */}
          <div className="mb-6 glass rounded-2xl border border-border overflow-hidden">
            <div className="p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
                  <Server className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <h1 className="text-lg font-black">{server.name}</h1>
                    <span className={`flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full border ${server.status === 'running' ? 'bg-green-500/10 text-green-500 border-green-500/25' : 'bg-red-500/10 text-red-500 border-red-500/25'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${statusBg} ${server.status === 'running' ? 'animate-pulse' : ''}`} />
                      {statusLabel}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5 flex items-center flex-wrap gap-x-1">
                    <BlurReveal value={server.ip4} mono small copyable={false} />
                    <span className="font-mono">·</span>
                    <span className="font-mono">{server.location}</span>
                    <span className="font-mono">·</span>
                    <span className="font-mono">{server.os.name}</span>
                  </p>
                </div>
              </div>

              {/* Power Controls */}
              <div className="flex items-center gap-2 shrink-0">
                {server.status === 'stopped' ? (
                  <button
                    onClick={() => powerAction('start')} disabled={!!powerLoading}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-xl font-bold text-sm disabled:opacity-50 transition-colors"
                    data-testid="power-start"
                  >
                    {powerLoading === 'start' ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Play className="w-4 h-4" />}
                    Starten
                  </button>
                ) : (
                  <>
                    <button onClick={() => powerAction('restart')} disabled={!!powerLoading}
                      className="flex items-center gap-2 px-3 py-2 bg-muted hover:bg-muted/80 rounded-xl font-bold text-sm disabled:opacity-50 transition-colors" data-testid="power-restart">
                      {powerLoading === 'restart' ? <span className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                      <span className="hidden sm:inline">Restart</span>
                    </button>
                    <button onClick={() => powerAction('reset')} disabled={!!powerLoading}
                      className="flex items-center gap-2 px-3 py-2 bg-yellow-500/10 hover:bg-yellow-500/20 text-yellow-500 border border-yellow-500/25 rounded-xl font-bold text-sm disabled:opacity-50 transition-colors" data-testid="power-reset">
                      {powerLoading === 'reset' ? <span className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" /> : <Zap className="w-4 h-4" />}
                      <span className="hidden sm:inline">Reset</span>
                    </button>
                    <button onClick={() => powerAction('stop')} disabled={!!powerLoading}
                      className="flex items-center gap-2 px-3 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-500 border border-red-500/25 rounded-xl font-bold text-sm disabled:opacity-50 transition-colors" data-testid="power-stop">
                      {powerLoading === 'stop' ? <span className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" /> : <Square className="w-4 h-4" />}
                      <span className="hidden sm:inline">Stop</span>
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Tabs */}
            <div className="border-t border-border overflow-x-auto hide-scrollbar">
              <div className="flex min-w-max">
                {tabs.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    data-testid={`tab-${tab.id}`}
                    className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'text-primary border-primary'
                        : 'text-muted-foreground border-transparent hover:text-foreground hover:border-border'
                    }`}
                  >
                    <tab.icon className="w-4 h-4 shrink-0" />
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Tab Content */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.15 }}
            >
              {activeTab === 'overview'   && <TabOverview server={server} />}
              {activeTab === 'console'    && <TabConsole server={server} />}
              {activeTab === 'backups'    && <TabBackups />}
              {activeTab === 'snapshots'  && <TabSnapshots />}
              {activeTab === 'os-install' && <TabOsInstall />}
              {activeTab === 'rescue'     && <TabRescue server={server} />}
              {activeTab === 'network'    && <TabNetwork server={server} />}
            </motion.div>
          </AnimatePresence>

          {/* Footer Note */}
          <div className="mt-8 text-center text-xs text-muted-foreground">
            <span>Demo-Ansicht – </span>
            <a href="/design-system" className="text-primary hover:underline">Design System ansehen</a>
            <span> · </span>
            <button
              className="text-primary hover:underline"
              onClick={() => {
                toast.info('Template-Paket: Datei im Browser-Tab öffnen und speichern');
                window.open('/whmcs-template/README.md', '_blank');
              }}
            >
              Installations-Anleitung
            </button>
            <span> · DeinServerHost WHMCS-Integration</span>
          </div>

        </main>
      </div>
    </div>
  );
}

// Missing import fix
function Palette({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="13.5" cy="6.5" r=".5" fill="currentColor"/>
      <circle cx="17.5" cy="10.5" r=".5" fill="currentColor"/>
      <circle cx="8.5" cy="7.5" r=".5" fill="currentColor"/>
      <circle cx="6.5" cy="12.5" r=".5" fill="currentColor"/>
      <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z"/>
    </svg>
  );
}
